import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { getControlName, service, useAuthConfig } from "@theprelude/core";
import {
  Button,
  Dropdown,
  DropdownContent,
  DropdownTrigger,
  EditorIcon,
  Form,
  IconButton,
  InputFormField,
  notifyError,
  notifySuccess,
} from "@theprelude/ds";
import { ValidControlCode } from "@theprelude/sdk";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { usePopper } from "@/hooks/use-popper";
import { controlInstanceSchema } from "@/lib/validators";

const formSchema = controlInstanceSchema.extend({ name: z.string() });

type FormSchema = z.infer<typeof formSchema>;

interface EditIntegrationDropdownProps {
  instanceId: string;
  control: ValidControlCode;
  name: string;
  className?: string;
}

export const EditIntegrationDropdown = ({
  className,
  ...props
}: EditIntegrationDropdownProps) => {
  const dropdown = usePopper();

  return (
    <Dropdown {...dropdown}>
      <DropdownTrigger asChild>
        <IconButton icon={<EditorIcon />} className={className} />
      </DropdownTrigger>

      <DropdownContent>
        <EditIntegrationForm
          {...props}
          onRequestClose={() => dropdown.onOpenChange(false)}
        />
      </DropdownContent>
    </Dropdown>
  );
};

interface EditIntegrationFormProps
  extends Omit<EditIntegrationDropdownProps, "className"> {
  onRequestClose: () => void;
}

const EditIntegrationForm = ({
  instanceId,
  control,
  name,
  onRequestClose,
}: EditIntegrationFormProps) => {
  const authConfig = useAuthConfig();

  const form = useForm<FormSchema>({
    defaultValues: { instanceId, control, name },
    resolver: zodResolver(formSchema),
  });

  const { register } = form;

  const { mutate: renameIntegration, isPending } = useMutation({
    mutationFn: (data: FormSchema) =>
      service(authConfig).partner.renamePartner({
        partnerCode: data.control,
        instanceId: data.instanceId,
        name: data.name,
      }),
    onSuccess: (data, variables) => {
      notifySuccess({
        message: `${getControlName(variables.control)} integration has been renamed to "${variables.name}".`,
      });
      onRequestClose();
    },
    onError: (error) => {
      notifyError({
        error,
        message: `Error occurred while trying to rename this ${getControlName(control)} integration.`,
      });
    },
  });

  const onSubmit = (data: FormSchema) => renameIntegration(data);

  return (
    <Form
      {...form}
      onSubmitValid={onSubmit}
      className="flex items-start gap-2 p-2"
    >
      <InputFormField
        {...register("name")}
        placeholder="Integration name"
        autoFocus
      />
      <Button
        type="submit"
        intent="primary"
        loading={isPending}
        disabled={isPending}
      >
        Save
      </Button>
    </Form>
  );
};
